import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Alert, Box, CircularProgress, Paper, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  AlertsDetailActivity,
  AlertsDetailRecipient,
  AlertsEmailDelivery,
  AlertsEmailLogEvent,
  AlertsPersonRef,
  AlertsPushDelivery,
  AlertsPushLog,
} from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import {
  AlertsAlertView,
  AlertsDashboardStore,
} from "../../../../stores/dashboards/AlertsDashboardStore";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import { AlertsPill } from "./AlertsDataTable";
import {
  AlertsInfoGrid,
  AlertsInlineLink,
  AlertsSimpleColumn,
  AlertsSimpleTable,
  AlertsToggleRow,
} from "./AlertsDetailParts";
import { AlertsEmptyBox } from "./AlertsListPanel";
import {
  ALERTS_EMPTY_VALUE,
  formatAlertsCount,
  formatAlertsDateTime,
  formatAlertsText,
} from "./alertsDashboardFormat";

type DetailTab = "recipients" | "timeline" | "push" | "email";

const personName = (person: AlertsPersonRef | null | undefined): string => {
  if (!person) {
    return "System";
  }
  if (person.kind === "SYSTEM") {
    return "System";
  }
  return person.name || person.email || `#${person.id ?? "?"}`;
};

export const AlertsEmailLogTable = ({
  logs,
}: {
  logs: AlertsEmailLogEvent[];
}) => {
  if (logs.length === 0) {
    return (
      <AlertsEmptyBox message="No send attempts or SendGrid events yet." />
    );
  }
  const columns: AlertsSimpleColumn<AlertsEmailLogEvent>[] = [
    { label: "When", render: (log) => formatAlertsDateTime(log.created_at) },
    { label: "Event", render: (log) => <AlertsPill value={log.event} /> },
    {
      label: "Attempt",
      render: (log) => (log.attempt == null ? "Webhook" : String(log.attempt)),
    },
    { label: "Detail", render: (log) => formatAlertsText(log.detail) },
    {
      label: "Message ID",
      render: (log) => formatAlertsText(log.provider_message_id),
    },
    {
      label: "Event ID",
      render: (log) => formatAlertsText(log.provider_event_id),
    },
  ];
  return (
    <AlertsSimpleTable
      columns={columns}
      rows={logs}
      rowKey={(log) => String(log.id)}
    />
  );
};

const PushLogTable = ({ logs }: { logs: AlertsPushLog[] }) => {
  if (logs.length === 0) {
    return <AlertsEmptyBox message="No device attempts yet." />;
  }
  const columns: AlertsSimpleColumn<AlertsPushLog>[] = [
    { label: "When", render: (log) => formatAlertsDateTime(log.created_at) },
    { label: "Attempt", render: (log) => String(log.attempt) },
    { label: "Platform", render: (log) => formatAlertsText(log.platform) },
    { label: "Token", render: (log) => `#${log.push_token_id}` },
    { label: "Result", render: (log) => <AlertsPill value={log.result} /> },
    { label: "Error", render: (log) => formatAlertsText(log.error_code) },
    { label: "FCM ID", render: (log) => formatAlertsText(log.fcm_message_id) },
  ];
  return (
    <AlertsSimpleTable
      columns={columns}
      rows={logs}
      rowKey={(log) => String(log.id)}
    />
  );
};

interface AlertsAlertDetailProps {
  store: AlertsDashboardStore;
  view: AlertsAlertView;
}

/** `POST /alert/:alertId` — header, then Recipients / Timeline / Push / Email. */
const AlertsAlertDetail: React.FC<AlertsAlertDetailProps> = ({
  store,
  view,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const [tab, setTab] = useState<DetailTab>("recipients");
  const [expanded, setExpanded] = useState<string | null>(null);
  const detail = view.result;

  useEffect(() => {
    setTab("recipients");
    setExpanded(null);
  }, [view.id]);

  if (view.loading && !detail) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 220,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!detail) {
    return view.error ? (
      <Alert
        severity="error"
        sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
      >
        {view.error}
      </Alert>
    ) : null;
  }

  const { alert, counts } = detail;

  const changeTab = (next: DetailTab) => {
    store.track("detail_tab_change", {
      view: "alert",
      title: alert.title,
      tab: next,
    });
    setTab(next);
    setExpanded(null);
  };

  const tabs: Array<{ id: DetailTab; label: string }> = [
    { id: "recipients", label: `Recipients (${detail.recipients.length})` },
    { id: "timeline", label: `Timeline (${detail.activity.length})` },
  ];
  if (alert.audience === "ADMIN" || detail.push_deliveries.length > 0) {
    tabs.push({ id: "push", label: `Push (${detail.push_deliveries.length})` });
  }
  if (alert.audience === "CUSTOMER" || detail.email_deliveries.length > 0) {
    tabs.push({
      id: "email",
      label: `Email (${detail.email_deliveries.length})`,
    });
  }

  const tick = (value: string | null) =>
    value ? (
      <Box
        component="span"
        title={formatAlertsDateTime(value)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <CheckIcon sx={{ fontSize: 14, color: customColors.successGreen }} />
        {formatAlertsDateTime(value)}
      </Box>
    ) : (
      ALERTS_EMPTY_VALUE
    );

  const recipientColumns: AlertsSimpleColumn<AlertsDetailRecipient>[] = [
    {
      label: "Name",
      render: (row) => (
        <AlertsInlineLink
          label={row.name || row.email || `#${row.id}`}
          onClick={() => void store.openRecipient(row.kind, row.id, row.name)}
        />
      ),
    },
    { label: "Kind", render: (row) => <AlertsPill value={row.kind} /> },
    { label: "Email", render: (row) => formatAlertsText(row.email) },
    { label: "Reason", render: (row) => formatAlertsText(row.reason) },
    { label: "Read", render: (row) => tick(row.read_at) },
    { label: "Archived", render: (row) => tick(row.archived_at) },
    { label: "Added", render: (row) => formatAlertsDateTime(row.created_at) },
  ];

  const activityColumns: AlertsSimpleColumn<AlertsDetailActivity>[] = [
    { label: "When", render: (row) => formatAlertsDateTime(row.created_at) },
    { label: "Action", render: (row) => <AlertsPill value={row.action} /> },
    {
      label: "By",
      render: (row) =>
        row.actor.kind !== "SYSTEM" && row.actor.id != null ? (
          <AlertsInlineLink
            label={personName(row.actor)}
            onClick={() =>
              void store.openRecipient(
                row.actor.kind === "ADMIN" ? "ADMIN" : "CUSTOMER_USER",
                row.actor.id as number,
                row.actor.name,
              )
            }
          />
        ) : (
          personName(row.actor)
        ),
    },
    { label: "Platform", render: (row) => formatAlertsText(row.platform) },
    { label: "Note", render: (row) => formatAlertsText(row.note) },
  ];

  const pushColumns: AlertsSimpleColumn<AlertsPushDelivery>[] = [
    {
      label: "Admin",
      render: (row) => (
        <AlertsInlineLink
          label={row.admin_name || `#${row.admin_id}`}
          onClick={() =>
            void store.openRecipient("ADMIN", row.admin_id, row.admin_name)
          }
        />
      ),
    },
    { label: "Channel", render: (row) => formatAlertsText(row.channel) },
    { label: "Status", render: (row) => <AlertsPill value={row.status} /> },
    { label: "Attempts", render: (row) => String(row.attempts) },
    { label: "Devices tried", render: (row) => String(row.logs.length) },
    { label: "Sent", render: (row) => formatAlertsDateTime(row.sent_at) },
    {
      label: "Next attempt",
      render: (row) => formatAlertsDateTime(row.next_attempt_at),
    },
    { label: "Last error", render: (row) => formatAlertsText(row.last_error) },
  ];

  const emailColumns: AlertsSimpleColumn<AlertsEmailDelivery>[] = [
    {
      label: "User",
      render: (row) => (
        <AlertsInlineLink
          label={row.user_name || `#${row.user_id}`}
          onClick={() =>
            void store.openRecipient(
              "CUSTOMER_USER",
              row.user_id,
              row.user_name,
            )
          }
        />
      ),
    },
    { label: "Delivery", render: (row) => <AlertsPill value={row.status} /> },
    {
      label: "Email",
      render: (row) =>
        row.batch ? (
          <AlertsInlineLink
            label={`#${row.batch.batch_id}`}
            onClick={() => void store.openEmail(row.batch?.batch_id as number)}
          />
        ) : (
          "Not batched yet"
        ),
    },
    {
      label: "Email status",
      render: (row) => <AlertsPill value={row.batch?.status} />,
    },
    { label: "To", render: (row) => formatAlertsText(row.batch?.to_email) },
    { label: "Events", render: (row) => String(row.batch?.logs.length ?? 0) },
    { label: "Last error", render: (row) => formatAlertsText(row.last_error) },
    { label: "Updated", render: (row) => formatAlertsDateTime(row.updated_at) },
  ];

  const toggleExpanded = (key: string) =>
    setExpanded((current) => (current === key ? null : key));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {view.error ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
        >
          {view.error}
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: customColors.softCardBorder }}
      >
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
          <AlertsPill value={alert.severity} />
          <AlertsPill value={alert.status} />
          <AlertsPill value={alert.audience} />
        </Box>
        {alert.body ? (
          <CustomTypography
            variant="body2"
            sx={{
              color: customColors.navyText,
              whiteSpace: "pre-wrap",
              mb: 1.5,
            }}
          >
            {alert.body}
          </CustomTypography>
        ) : null}
        <AlertsInfoGrid
          items={[
            { label: "Alert ID", value: String(alert.id) },
            { label: "Type", value: alert.type_code },
            { label: "Type description", value: alert.type_description },
            {
              label: "Section",
              value: alert.section_label || alert.section_code,
            },
            {
              label: alert.ref_type_label || alert.ref_type || "Ref",
              value: alert.ref_display,
            },
            {
              label: "Customer",
              value: alert.customer_id
                ? `${alert.customer_name || "Customer"} (#${alert.customer_id})`
                : null,
            },
            {
              label: "Group",
              value:
                alert.group_id != null
                  ? `#${alert.group_id} · ${formatAlertsCount(alert.group_size)} alerts`
                  : null,
            },
            { label: "Source", value: alert.source },
            {
              label: "Occurrences",
              value: formatAlertsCount(alert.occurrence_count),
            },
            { label: "Created", value: formatAlertsDateTime(alert.created_at) },
            {
              label: "First occurred",
              value: formatAlertsDateTime(alert.first_occurred_at),
            },
            {
              label: "Last occurred",
              value: formatAlertsDateTime(alert.last_occurred_at),
            },
            { label: "Raised by", value: personName(alert.actor) },
            { label: "Resolvable", value: alert.is_resolvable ? "Yes" : "No" },
            {
              label: "Resolved",
              value: alert.resolved_at
                ? `${formatAlertsDateTime(alert.resolved_at)} · ${personName(alert.resolved_by)}`
                : null,
            },
            { label: "Resolution", value: alert.resolution_source },
            { label: "Resolution note", value: alert.resolution_note },
            {
              label: "Suppressed until",
              value: alert.suppressed_until
                ? formatAlertsDateTime(alert.suppressed_until)
                : null,
            },
            {
              label: "Inbox",
              value: `${counts.recipients} recipients · ${counts.read} read · ${counts.archived} archived`,
            },
            {
              label: "Push",
              value: `${counts.push.SENT} sent · ${counts.push.FAILED} failed · ${counts.push.SKIPPED} skipped · ${counts.push.PENDING} pending`,
            },
            {
              label: "Email deliveries",
              value: `${counts.email.DELIVERED} delivered · ${counts.email.SENT} sent · ${counts.email.BOUNCED} bounced · ${counts.email.FAILED} failed · ${counts.email.SKIPPED} skipped · ${counts.email.QUEUED + counts.email.PENDING} pending`,
            },
          ]}
        />
      </Paper>

      <AlertsToggleRow options={tabs} value={tab} onChange={changeTab} />

      {tab === "recipients" ? (
        detail.recipients.length > 0 ? (
          <AlertsSimpleTable
            columns={recipientColumns}
            rows={detail.recipients}
            rowKey={(row) => `${row.kind}-${row.id}`}
          />
        ) : (
          <AlertsEmptyBox message="Nobody received this alert in-app." />
        )
      ) : null}

      {tab === "timeline" ? (
        detail.activity.length > 0 ? (
          <AlertsSimpleTable
            columns={activityColumns}
            rows={detail.activity}
            rowKey={(row) => String(row.id)}
          />
        ) : (
          <AlertsEmptyBox message="No activity recorded." />
        )
      ) : null}

      {tab === "push" ? (
        detail.push_deliveries.length > 0 ? (
          <>
            <CustomTypography
              variant="body4"
              sx={{ color: customColors.mutedSlateText }}
            >
              Click a delivery to see each device attempt.
            </CustomTypography>
            <AlertsSimpleTable
              columns={pushColumns}
              rows={detail.push_deliveries}
              rowKey={(row) => `push-${row.delivery_id}`}
              onRowClick={(row) => toggleExpanded(`push-${row.delivery_id}`)}
              expandedKey={expanded}
              renderExpanded={(row) => <PushLogTable logs={row.logs} />}
            />
          </>
        ) : (
          <AlertsEmptyBox message="No push deliveries for this alert." />
        )
      ) : null}

      {tab === "email" ? (
        detail.email_deliveries.length > 0 ? (
          <>
            <CustomTypography
              variant="body4"
              sx={{ color: customColors.mutedSlateText }}
            >
              Click a delivery to see its email and SendGrid events.
            </CustomTypography>
            <AlertsSimpleTable
              columns={emailColumns}
              rows={detail.email_deliveries}
              rowKey={(row) => `email-${row.delivery_id}`}
              onRowClick={(row) => toggleExpanded(`email-${row.delivery_id}`)}
              expandedKey={expanded}
              renderExpanded={(row) =>
                row.batch ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    <AlertsInfoGrid
                      items={[
                        { label: "Email", value: `#${row.batch.batch_id}` },
                        {
                          label: "Status",
                          value: <AlertsPill value={row.batch.status} />,
                        },
                        {
                          label: "Attempts",
                          value: String(row.batch.attempts),
                        },
                        {
                          label: "Alerts in email",
                          value: formatAlertsCount(row.batch.alerts),
                        },
                        {
                          label: "Enqueued",
                          value: formatAlertsDateTime(row.batch.enqueued_at),
                        },
                        {
                          label: "Sent",
                          value: formatAlertsDateTime(row.batch.sent_at),
                        },
                        {
                          label: "Delivered",
                          value: formatAlertsDateTime(row.batch.delivered_at),
                        },
                        {
                          label: "Failed",
                          value: formatAlertsDateTime(row.batch.failed_at),
                        },
                        {
                          label: "Next attempt",
                          value: formatAlertsDateTime(
                            row.batch.next_attempt_at,
                          ),
                        },
                        {
                          label: "Message ID",
                          value: row.batch.provider_message_id,
                        },
                        { label: "Last error", value: row.batch.last_error },
                      ]}
                    />
                    <AlertsEmailLogTable logs={row.batch.logs} />
                  </Box>
                ) : (
                  <AlertsEmptyBox message="Not batched into an email yet." />
                )
              }
            />
          </>
        ) : (
          <AlertsEmptyBox message="No email deliveries for this alert." />
        )
      ) : null}
    </Box>
  );
};

export default observer(AlertsAlertDetail);
